import React, { useState, useRef } from 'react';
import DashboardLayout from '../layout/DashboardLayout';
import Table from '../dashboard/Table';
import DataDisplay from '../dashboard/DataDisplay';
import DashboardHeader from '../dashboard/DashboardHeader';
import TableControls from '../dashboard/TableControls';
import { useNavigate } from 'react-router-dom';
import NewLead from './NewLead';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLeads, loadColumnPreferences, saveColumnPreferences, updateLead } from '../../store/slices/leadsSlice';
import { fetchBannerMessage } from '../../store/slices/settingsSlice';
import { saveColumnOrder, fetchColumnOrder } from '../../store/slices/usersSlice';
import LeadsCustomizeFieldsDialog from './LeadsCustomizeFieldsDialog';
import { setSearchLoading } from '../../store/slices/searchSlice';
import { setProjectManagerFilter, clearProjectManagerFilter } from '../../store/slices/viewSlice';
import Spinner from '../common/Spinner';
import LeadDetailsDialog from './LeadDetailsDialog';
import CustomizeFieldsDialog from '../dashboard/CustomizeFieldsDialog';
import LeadStatsCards from './LeadStatsCards';
import { DocumentIcon } from '@heroicons/react/24/outline';

function LeadsAndEstimates() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = React.useState({
    key: null,
    direction: 'asc'
  });
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchColumn, setSearchColumn] = React.useState('all');
  const [isCustomizeFieldsOpen, setIsCustomizeFieldsOpen] = React.useState(false);
  const [isNewLeadOpen, setIsNewLeadOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('all_active');
  const [selectedLeads, setSelectedLeads] = React.useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isLeadDetailsOpen, setIsLeadDetailsOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  
  // Redux selectors
  const { leads, loading, pagination, columnPreferences } = useSelector(state => state.leads);
  const { columnOrders } = useSelector(state => state.users);
  const { filters } = useSelector(state => state.view);
  const { bannerMessage } = useSelector(state => state.settings);
  const { user } = useSelector(state => state.auth);
  
  // Project Manager Filter State
  const selectedProjectManager = filters?.leads?.selectedProjectManager || null;
  const [showProjectManagerDropdown, setShowProjectManagerDropdown] = useState(false);
  const [pmSearchTerm, setPmSearchTerm] = useState('');
  const projectManagerDropdownRef = useRef(null);

  // Date Filter State
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerDropdownRef = useRef(null);
  const suppressNextAutoLoadRef = useRef(false);

  // Quick filter state (driven by the "Filters" button popover in DashboardHeader)
  const [selectedPriority, setSelectedPriority] = useState(null); // 'urgent' | 'high' | 'normal' | 'low' | null

  const clearAllFilters = () => {
    setSelectedPriority(null);
    dispatch(clearProjectManagerFilter({ page: 'leads' }));
    setDateRange({ startDate: null, endDate: null });
  };

  // Define default columns
  const defaultColumns = [
    { id: 1, key: 'jobNumber', label: 'Job Number', name: 'Job Number', active: true, checked: true },
    // Lead Contact Information (Step 1)
    { id: 2, key: 'leadContact.name', label: 'Lead Contact Name', name: 'Lead Contact Name', active: true, checked: true },
    { id: 3, key: 'leadContact.phone', label: 'Lead Contact Phone', name: 'Lead Contact Phone', active: true, checked: true },
    { id: 4, key: 'leadContact.email', label: 'Lead Contact Email', name: 'Lead Contact Email', active: true, checked: true },
    // Address Information
    { id: 5, key: 'address.city', label: 'City', name: 'City', active: true, checked: true },
    // Optional Client Information
    { id: 6, key: 'client.name', label: 'Client Name', name: 'Client Name', active: true, checked: false },
    { id: 7, key: 'client.cellPhone', label: 'Client Phone', name: 'Client Phone', active: true, checked: false },
    { id: 8, key: 'client.altPhone', label: 'Client Alt Phone', name: 'Client Alt Phone', active: true, checked: false },
    { id: 9, key: 'client.email', label: 'Client Email', name: 'Client Email', active: true, checked: false },
    { id: 10, key: 'client.address.city', label: 'Client City', name: 'Client City', active: true, checked: false },
    // Billing Information
    { id: 22, key: 'billingContact.name', label: 'Billing Contact Name', name: 'Billing Contact Name', active: true, checked: false },
    { id: 23, key: 'billingContact.phone', label: 'Billing Phone', name: 'Billing Phone', active: true, checked: false },
    { id: 24, key: 'billingContact.email', label: 'Billing Email', name: 'Billing Email', active: true, checked: false },
    { id: 25, key: 'billingContact.address.street', label: 'Billing Address', name: 'Billing Address', active: true, checked: false },
    { id: 26, key: 'billingContact.address.city', label: 'Billing City', name: 'Billing City', active: true, checked: false },
    { id: 27, key: 'billingContact.address.state', label: 'Billing State', name: 'Billing State', active: true, checked: false },
    { id: 28, key: 'billingContact.address.zipCode', label: 'Billing Zip Code', name: 'Billing Zip Code', active: true, checked: false },
    // Site Contact Information
    { id: 11, key: 'siteContact.name', label: 'Site Contact Name', name: 'Site Contact Name', active: true, checked: false },
    { id: 12, key: 'siteContact.email', label: 'Site Contact Email', name: 'Site Contact Email', active: true, checked: false },
    { id: 13, key: 'siteContact.cellPhone', label: 'Site Contact Phone', name: 'Site Contact Phone', active: true, checked: false },
    { id: 14, key: 'siteContact.altPhone', label: 'Site Contact Alt Phone', name: 'Site Contact Alt Phone', active: true, checked: false },
    { id: 15, key: 'siteContact.address.city', label: 'Site Contact City', name: 'Site Contact City', active: true, checked: false },
    // Status and Priority
    { id: 16, key: 'leadStatus', label: 'Lead Status', name: 'Lead Status', active: true, checked: true },
    { id: 17, key: 'priority', label: 'Priority', name: 'Priority', active: true, checked: true },
    { id: 18, key: 'mapLink', label: 'Map Link', name: 'Map Link', active: true, checked: false },
    // Tracking Status
    { id: 29, key: 'trackingStatus', label: 'Tracking Status', name: 'Tracking Status', active: true, checked: true },
    // Timestamps
    { id: 19, key: 'createdAt', label: 'Created Date', name: 'Created Date', active: true, checked: true },
    { id: 20, key: 'lastModified', label: 'Last Modified', name: 'Last Modified', active: true, checked: true },
    { id: 21, key: 'isArchived', label: 'Archived', name: 'Archived', active: true, checked: false },
    { id: 30, key: 'lastProposalSent', label: 'Last Proposal Sent', name: 'Last Proposal Sent', active: true, checked: true }
  ];

  // Initialize with default columns
  const [visibleColumns, setVisibleColumns] = React.useState(defaultColumns.filter(col => col.checked));
  const [activeColumns, setActiveColumns] = React.useState(defaultColumns);
  const [preferencesLoaded, setPreferencesLoaded] = React.useState(false);

  // Helper function to get initials
  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last;
  };

  // Click outside handler for dropdowns
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (projectManagerDropdownRef.current && !projectManagerDropdownRef.current.contains(event.target)) {
        setShowProjectManagerDropdown(false);
        setPmSearchTerm('');
      }
      if (datePickerDropdownRef.current && !datePickerDropdownRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Function to get all unique project managers from leads
  const getAllProjectManagers = () => {
    const managersMap = new Map();
    leads?.forEach(lead => {
      lead.projectManagers?.forEach(pm => {
        if (pm && pm._id && pm.profile && pm.profile.firstName && pm.profile.lastName) {
          managersMap.set(pm._id, pm);
        }
      });
    });
    return Array.from(managersMap.values()).sort((a, b) => {
      const nameA = `${a.profile?.firstName || ''} ${a.profile?.lastName || ''}`.toLowerCase();
      const nameB = `${b.profile?.firstName || ''} ${b.profile?.lastName || ''}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  // Function to filter project managers based on search term
  const getFilteredProjectManagers = () => {
    const allManagers = getAllProjectManagers();
    if (!pmSearchTerm.trim()) {
      return allManagers;
    }
    const searchLower = pmSearchTerm.toLowerCase();
    return allManagers.filter(manager => {
      const fullName = `${manager.profile?.firstName || ''} ${manager.profile?.lastName || ''}`.toLowerCase();
      const role = manager.profile?.role?.replace('_', ' ').toLowerCase() || '';
      return fullName.includes(searchLower) || role.includes(searchLower);
    });
  };

  // Helper functions for Redux project manager filter
  const handleSetProjectManager = (manager) => {
    dispatch(setProjectManagerFilter({ page: 'leads', projectManager: manager }));
    setPmSearchTerm('');
    setShowProjectManagerDropdown(false);
  };

  const handleClearProjectManager = () => {
    dispatch(clearProjectManagerFilter({ page: 'leads' }));
    setPmSearchTerm('');
    setShowProjectManagerDropdown(false);
  };

  // Helper functions for date filtering
  const getDateRangeDisplay = () => {
    const { startDate, endDate } = dateRange;
    if (!startDate && !endDate) return 'All Time';
    if (startDate && endDate) {
      return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
    }
    if (startDate) return `From ${startDate.toLocaleDateString()}`;
    if (endDate) return `Until ${endDate.toLocaleDateString()}`;
    return 'All Time';
  };

  const clearDateRange = () => {
    setDateRange({ startDate: null, endDate: null });
  };

  // Load preferences only once on mount
  React.useEffect(() => {
    console.log('🚀 useEffect triggered - preferencesLoaded:', preferencesLoaded);
    
    const loadPreferences = async () => {
      // Skip if preferences are already loaded
      if (preferencesLoaded) {
        console.log('⏭️ Skipping - preferences already loaded');
        return;
      }

      console.log('🔄 Loading lead preferences and column order...');

      try {
        console.log('📞 About to call loadColumnPreferences...');
        const preferencesPromise = dispatch(loadColumnPreferences()).unwrap();
        
        console.log('📞 About to call fetchColumnOrder...');
        const orderPromise = dispatch(fetchColumnOrder('leads')).unwrap();
        
        console.log('⏳ Waiting for both API calls...');
        const [preferencesResult, orderResult] = await Promise.all([
          preferencesPromise,
          orderPromise
        ]);
        
        console.log('📊 Preferences result:', preferencesResult);
        console.log('📋 Order result:', orderResult);
        
        let columnsToProcess = defaultColumns;
        
        // First apply column preferences (checked/unchecked status)
        if (preferencesResult?.preferences?.leadColumns?.length) {
          columnsToProcess = defaultColumns.map(col => {
            const savedPref = preferencesResult.preferences.leadColumns.find(pref => parseInt(pref.id) === col.id);
            if (savedPref) {
              return {
                ...col,
                checked: savedPref.checked,
                active: savedPref.active
              };
            }
            return col;
          });
          console.log('✅ Applied column preferences');
        }
        
        // Then apply column order if available
        const savedOrder = orderResult?.columnOrder || [];
        console.log('🔍 Saved column order:', savedOrder);
        console.log('🔍 Available column keys:', columnsToProcess.map(c => c.key));
        
        if (savedOrder && savedOrder.length > 0) {
          const orderedColumns = [];
          const columnMap = new Map(columnsToProcess.map(col => [col.key, col]));
          
          console.log('🗺️ Column map keys:', Array.from(columnMap.keys()));
          
          // Add columns in the saved order
          savedOrder.forEach(key => {
            if (columnMap.has(key)) {
              orderedColumns.push(columnMap.get(key));
              columnMap.delete(key);
              console.log(`✅ Added column in order: ${key}`);
            } else {
              console.log(`⚠️ Column key not found: ${key}`);
            }
          });
          
          // Add any remaining columns that weren't in the saved order
          columnMap.forEach(col => {
            orderedColumns.push(col);
            console.log(`➕ Added remaining column: ${col.key}`);
          });
          
          console.log('🎯 Final ordered columns:', orderedColumns.map(c => ({ key: c.key, label: c.label })));
          
          setActiveColumns(orderedColumns);
          setVisibleColumns(orderedColumns.filter(col => col.checked));
          console.log('✅ Applied saved column order');
        } else {
          console.log('ℹ️ No saved column order found, using default');
          setActiveColumns(columnsToProcess);
          setVisibleColumns(columnsToProcess.filter(col => col.checked));
        }
        
        setPreferencesLoaded(true);
        console.log('✅ Preferences loaded successfully');
      } catch (error) {
        console.error('❌ Failed to load preferences:', error);
        console.error('❌ Error details:', error.message);
        console.error('❌ Error stack:', error.stack);
        // Use default columns on error
        setActiveColumns(defaultColumns);
        setVisibleColumns(defaultColumns.filter(col => col.checked));
        setPreferencesLoaded(true);
      }
    };
    
    loadPreferences();
  }, [dispatch, preferencesLoaded]);

  // Load leads and banner message when component mounts or when dependencies change
  React.useEffect(() => {
    if (suppressNextAutoLoadRef.current) {
      suppressNextAutoLoadRef.current = false;
      return;
    }

    loadLeads();
    dispatch(fetchBannerMessage());
  }, [currentPage, itemsPerPage, sortConfig, searchTerm, searchColumn, activeTab, selectedProjectManager, dateRange, selectedPriority, dispatch]);

  // Handle banner auto-hide
  React.useEffect(() => {
    if (bannerMessage?.isEnabled && bannerMessage?.autoHideAfter > 0) {
      const timer = setTimeout(() => {
        setShowBanner(false);
      }, bannerMessage.autoHideAfter * 60 * 1000); // Convert minutes to milliseconds

      return () => clearTimeout(timer);
    }
  }, [bannerMessage]);

  const loadLeads = async (overrides = {}) => {
    const effectiveSearchTerm = overrides.searchTerm ?? searchTerm;
    const effectiveSearchColumn = overrides.searchColumn ?? searchColumn;
    const effectivePage = overrides.page ?? (itemsPerPage === 'all' ? 1 : currentPage);
    const effectiveLimit = overrides.limit ?? (itemsPerPage === 'all' ? 1000000 : itemsPerPage);

    const params = {
      page: effectivePage,
      limit: effectiveLimit,
      ...(sortConfig.key && {
        sortField: sortConfig.key,
        sortOrder: sortConfig.direction
      }),
      ...(effectiveSearchTerm && { 
        search: effectiveSearchTerm,
        ...(effectiveSearchColumn !== 'all' && { searchField: effectiveSearchColumn })
      }),
      ...(selectedProjectManager && {
        projectManagerId: selectedProjectManager._id
      }),
      ...(dateRange.startDate && { startDate: dateRange.startDate.toISOString() }),
      ...(dateRange.endDate && { endDate: dateRange.endDate.toISOString() }),
      ...(selectedPriority && { priority: selectedPriority })
    };

    // Add tab-specific parameters
    switch (activeTab) {
      case 'all_active':
        // All active (non-archived) leads
        params.trackingStatus = 'all_active';
        // Sort by creation date (newest first) by default
        if (!sortConfig.key) {
          params.sortField = 'createdAt';
          params.sortOrder = 'desc';
        }
        break;
      case 'waiting':
        // Leads waiting for proposals (waiting & critical)
        params.trackingStatus = 'all_waiting';
        // Keep any existing sort if specified, otherwise sort by creation date (oldest first)
        if (!sortConfig.key) {
          params.sortField = 'createdAt';
          params.sortOrder = 'asc';
        }
        break;
      case 'recently_sent':
        // Leads with recently sent proposals
        params.trackingStatus = 'recently_sent';
        // Keep any existing sort if specified, otherwise sort by lastProposalSent (newest first)
        if (!sortConfig.key) {
          params.sortField = 'lastProposalSent';
          params.sortOrder = 'desc';
        }
        break;
      case 'critical':
        // Critical leads (more than 10 days without proposal)
        params.trackingStatus = 'critical';
        // Keep any existing sort if specified, otherwise sort by creation date (oldest first)
        if (!sortConfig.key) {
          params.sortField = 'createdAt';
          params.sortOrder = 'asc';
        }
        break;
      case 'follow_up':
        // Leads that need follow-up (2+ weeks after proposal)
        params.trackingStatus = 'follow_up';
        // Keep any existing sort if specified, otherwise sort by lastProposalSent (oldest first)
        if (!sortConfig.key) {
          params.sortField = 'lastProposalSent';
          params.sortOrder = 'asc';
        }
        break;
      case 'converted':
        // Converted leads
        params.trackingStatus = 'converted';
        // Keep any existing sort if specified, otherwise sort by conversion date (newest first)
        if (!sortConfig.key) {
          params.sortField = 'updatedAt';
          params.sortOrder = 'desc';
        }
        break;
      case 'ready_to_schedule':
        // Leads whose corresponding project is ready to be scheduled.
        // A lead becomes a ready_to_schedule project when its leadStatus is customer_approved.
        params.leadStatus = 'customer_approved';
        if (!sortConfig.key) {
          params.sortField = 'updatedAt';
          params.sortOrder = 'desc';
        }
        break;
      case 'archived':
        // Archived filtering remains the same
        params.isArchived = true;
        break;
      default:
        // Default to all active leads
        params.trackingStatus = 'all_active';
        break;
    }

    // Debug log to check params
    console.log('Fetching leads with params:', params);

    return dispatch(fetchLeads(params)).unwrap();
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleColumnToggle = (columnId) => {
    console.log('LeadsAndEstimates - Toggling column:', columnId);
    const updatedColumns = activeColumns.map(col => {
      if (col.id === columnId) {
        console.log('Toggling column from:', col.checked, 'to:', !col.checked);
        return { ...col, checked: !col.checked };
      }
      return col;
    });
    console.log('Updated columns:', updatedColumns);
    setActiveColumns(updatedColumns);
  };

  const handleApplyColumns = async (selectedColumns) => {
    try {
      // Ensure columns have all required properties
      const columnsWithKeys = selectedColumns.map(col => {
        const originalCol = defaultColumns.find(defaultCol => defaultCol.id === parseInt(col.id));
        return {
          ...col,
          key: originalCol?.key || col.key,
          label: originalCol?.label || col.label,
          name: originalCol?.name || col.name
        };
      });

      // Make sure we're sending an array of columns
      await dispatch(saveColumnPreferences({
        columns: Array.isArray(columnsWithKeys) ? columnsWithKeys : [],
        type: 'leads'
      })).unwrap();
      
      const updatedColumns = columnsWithKeys.filter(col => col.checked);
      setVisibleColumns(updatedColumns);
      setActiveColumns(columnsWithKeys);
      setIsCustomizeFieldsOpen(false);
    } catch (error) {
      console.error('Failed to save preferences:', error);
    }
  };

  // Helper function to get a user-friendly value for tracking status
  const getTrackingStatusDisplay = (status) => {
    const statusMap = {
      'new': 'New',
      'waiting': 'Waiting for Proposal',
      'recently_sent': 'Recently Sent',
      'critical': 'Critical',
      'follow_up': 'Follow Up',
      'converted': 'Converted',
      'lost': 'Lost'
    };
    return statusMap[status] || status;
  };

  // Transform lead data based on visible columns
  const transformLeadData = (lead) => {
    const transformed = {};
    visibleColumns.forEach(column => {
      if (!column.key) {
        console.warn('Column missing key property:', column);
        return;
      }

      // Handle nested properties
      const value = column.key.split('.').reduce((obj, key) => {
        if (obj === null || obj === undefined) return '';
        return obj[key];
      }, lead);

      // Special handling for leadContact fields
      if (column.key.startsWith('leadContact.')) {
        transformed[column.key] = value || 'N/A';
      } 
      // Handle optional client fields
      else if (column.key.startsWith('client.')) {
        transformed[column.key] = value || 'N/A';
      }
      // Handle billing contact fields
      else if (column.key.startsWith('billingContact.')) {
        transformed[column.key] = value || 'N/A';
      }
      // Format date fields with Los Angeles timezone
      else if (['createdAt', 'lastModified'].includes(column.key) && value) {
        transformed[column.key] = new Date(value).toLocaleString('en-US', {
          timeZone: 'America/Los_Angeles',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
      }
      // Special handling for lastProposalSent to ensure it displays properly
      else if (column.key === 'lastProposalSent') {
        transformed[column.key] = value ? new Date(value).toLocaleString('en-US', {
          timeZone: 'America/Los_Angeles',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        }) : 'None';
      }
      // Format tracking status to be more readable
      else if (column.key === 'trackingStatus') {
        transformed[column.key] = getTrackingStatusDisplay(value);
      }
      // Handle all other fields
      else {
        transformed[column.key] = value || 'N/A';
      }
    });
    transformed.id = lead._id;
    return transformed;
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1); // Reset to first page when changing tabs
    setSortConfig({ key: null, direction: 'asc' }); // Reset sort when changing tabs
    dispatch(clearProjectManagerFilter({ page: 'leads' })); // Reset project manager filter when changing tabs
  };

  const handleCheckRow = (id) => {
    setSelectedLeads(prev => {
      if (prev.includes(id)) {
        return prev.filter(leadId => leadId !== id);
      }
      return [...prev, id];
    });
  };

  const handleCheckAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(lead => lead._id));
    }
  };

  const handleCellUpdate = async (leadId, field, value) => {
    try {
      // Special handling for trackingStatus = 'lost'
      // We want to mark it as lost but not delete it
      if (field === 'trackingStatus' && value === 'lost') {
        await dispatch(updateLead({ 
          id: leadId, 
          updates: { 
            trackingStatus: 'lost',
            // Ensure the lead is not archived or deleted
            isArchived: false
          }
        })).unwrap();
      } else {
        await dispatch(updateLead({ 
          id: leadId, 
          updates: { [field]: value }
        })).unwrap();
      }
      
      // Refresh the leads data
      loadLeads();
    } catch (error) {
      console.error('Failed to update lead:', error);
    }
  };

  // Update search handler to manage loading state and handle columns
  const handleSearch = async (searchValue, columnKey = 'all') => {
    try {
      dispatch(setSearchLoading(true));

      // We fetch manually below, so suppress the next dependency-driven auto fetch.
      suppressNextAutoLoadRef.current = true;
      setSearchTerm(searchValue);
      setSearchColumn(columnKey);
      setCurrentPage(1); // Reset to first page when searching
      
      // Wait for the leads to load with the general search term
      const result = await loadLeads({
        searchTerm: searchValue,
        searchColumn: columnKey,
        page: 1
      });
      
      // If we have results and a specific column was selected, filter client-side
      const loadedLeads = result?.leads || [];
      if (searchValue && columnKey !== 'all' && loadedLeads.length > 0) {
        // Apply client-side filtering for specific column
        const filtered = loadedLeads.filter(lead => {
          // Handle nested properties like 'client.name' or 'address.city'
          if (columnKey.includes('.')) {
            const keys = columnKey.split('.');
            let value = lead;
            for (const key of keys) {
              if (!value || typeof value !== 'object') return false;
              value = value[key];
            }
            return value && String(value).toLowerCase().includes(searchValue.toLowerCase());
          } 
          // Handle special cases like projectManagers array
          else if (columnKey === 'projectManagers') {
            const managers = lead.projectManagers || [];
            return managers.some(pm => 
              (pm.profile?.firstName + ' ' + pm.profile?.lastName)
                .toLowerCase()
                .includes(searchValue.toLowerCase())
            );
          }
          // Regular properties
          else {
            return lead[columnKey] && 
              String(lead[columnKey]).toLowerCase().includes(searchValue.toLowerCase());
          }
        });
        
        // Update the state with filtered leads
        // This is a workaround since the backend doesn't support column-specific searching
        dispatch({
          type: 'leads/fetchLeads/fulfilled',
          payload: {
            leads: filtered,
            pagination: {
              ...(result?.pagination || pagination),
              total: filtered.length
            }
          }
        });
      }
    } finally {
      dispatch(setSearchLoading(false));
    }
  };

  // Add handler for row clicks
  const handleRowClick = (row) => {
    // Find the full lead data from the leads array
    const fullLead = leads.find(lead => lead._id === row.id);
    if (!fullLead) return;
    
    // Pass the raw lead data directly to LeadDetailsDialog
    setSelectedLead(fullLead);
    setIsLeadDetailsOpen(true);
  };

  // Add handler for opening new lead dialog
  const handleNewLead = () => {
    setIsNewLeadOpen(true);
  };

  // Define the tabs for the dashboard header
  // Note: "Ready to Schedule" and "Archives" belong in Projects, not Leads & Estimates
  const tabs = [
    { id: 'all_active', label: 'All Leads' },
    { id: 'waiting', label: 'Waiting For Proposal' },
    { id: 'recently_sent', label: 'Recently Sent' },
    { id: 'follow_up', label: 'Follow Up' },
    { id: 'critical', label: 'Critical' },
    { id: 'converted', label: 'Converted' }
  ];

  // Add column reorder handler
  const handleColumnReorder = async (newColumnOrder) => {
    try {
      console.log('🔄 Reordering columns:', newColumnOrder.map(c => ({ key: c.key, label: c.label })));
      
      // Update local state immediately
      setActiveColumns(newColumnOrder);
      setVisibleColumns(newColumnOrder.filter(col => col.checked));
      
      // Save to backend
      const columnKeys = newColumnOrder.map(col => col.key);
      console.log('💾 Saving column order:', columnKeys);
      await dispatch(saveColumnOrder({ type: 'leads', columnOrder: columnKeys })).unwrap();
      console.log('✅ Column order saved successfully');
    } catch (error) {
      console.error('❌ Failed to save column order:', error);
      // Revert on error
      setActiveColumns(activeColumns);
      setVisibleColumns(activeColumns.filter(col => col.checked));
    }
  };

  return (
    <DashboardLayout onNewLead={handleNewLead}>
      <div className="flex flex-col min-h-full">
        {/* Dynamic Banner Message */}
        {bannerMessage?.isEnabled && bannerMessage?.text && showBanner && 
          bannerMessage?.visibleTo?.includes(user?.profile?.role) && (
          <div className={`bg-${bannerMessage.backgroundColor}-50 border-b border-${bannerMessage.backgroundColor}-200`}>
            <div className="px-4 py-3 flex items-center justify-center">
              <svg className={`w-5 h-5 text-${bannerMessage.backgroundColor}-600 mr-2`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className={`text-sm text-${bannerMessage.backgroundColor}-700 font-medium`}>
                {bannerMessage.text}
              </p>
            </div>
          </div>
        )}
        <DashboardHeader 
          hideCreateLead={false} 
          onCustomizeFields={() => setIsCustomizeFieldsOpen(true)}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          selectedLeads={selectedLeads}
          onSearch={handleSearch}
          searchTerm={searchTerm}
          leads={leads}
          visibleColumns={visibleColumns}
          tabs={tabs}
          filters={{
            priority: selectedPriority,
            projectManager: selectedProjectManager,
            dateRange,
          }}
          onPriorityChange={setSelectedPriority}
          onClearAllFilters={clearAllFilters}
        />
        <div className="flex-1 min-h-0 p-3 sm:p-4 flex flex-col gap-3">
          {/* Stat Cards */}
          <LeadStatsCards leads={leads} totalCount={pagination?.total || 0} />

          <div className="flex items-center justify-between -mt-1">
            <span className="text-xs font-medium text-gray-500">
              <span className="text-gray-900 font-semibold">{(pagination?.total || 0).toLocaleString()}</span> Leads
            </span>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <Spinner size="xl" color="blue" />
            </div>
          ) : (
            <>
              {(!leads || leads.length === 0) ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <DocumentIcon className="w-16 h-16 mb-4" />
                  <p className="text-lg font-medium">No leads found</p>
                  <p className="text-sm">Create a new lead to get started</p>
                </div>
              ) : (
                <div className="flex-1 min-h-0 flex flex-col">
                  <div className="flex-1 min-h-0">
                    <DataDisplay 
                      columns={visibleColumns}
                      data={(leads || []).map(transformLeadData)}
                      onSort={handleSort}
                      sortConfig={sortConfig}
                      onCheckAll={handleCheckAll}
                      onCheckRow={handleCheckRow}
                      selectedRows={selectedLeads}
                      onUpdateCell={handleCellUpdate}
                      onRowClick={handleRowClick}
                      onColumnReorder={handleColumnReorder}
                      title="Leads"
                      // Remove pagination from DataDisplay
                      pagination={null}
                      // Project Manager Filter Props
                      showProjectManagerFilter={true}
                      selectedProjectManager={selectedProjectManager}
                      showProjectManagerDropdown={showProjectManagerDropdown}
                      setShowProjectManagerDropdown={setShowProjectManagerDropdown}
                      pmSearchTerm={pmSearchTerm}
                      setPmSearchTerm={setPmSearchTerm}
                      handleSetProjectManager={handleSetProjectManager}
                      handleClearProjectManager={handleClearProjectManager}
                      projectManagerDropdownRef={projectManagerDropdownRef}
                      getAllProjectManagers={getAllProjectManagers}
                      getFilteredProjectManagers={getFilteredProjectManagers}
                      getInitials={getInitials}
                      // Date Filter Props
                      showDateFilter={true}
                      dateRange={dateRange}
                      setDateRange={setDateRange}
                      showDatePicker={showDatePicker}
                      setShowDatePicker={setShowDatePicker}
                      datePickerDropdownRef={datePickerDropdownRef}
                      getDateRangeDisplay={getDateRangeDisplay}
                      clearDateRange={clearDateRange}
                    />
                  </div>
                  
                  {/* Separate Pagination Section */}
                  {pagination && pagination.total > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 bg-white rounded-lg">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        {/* Items per page selector */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                          <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600 whitespace-nowrap">Items per page:</label>
                            <select
                              value={itemsPerPage === 'all' ? 'all' : itemsPerPage}
                              onChange={(e) => {
                                const value = e.target.value === 'all' ? 'all' : parseInt(e.target.value);
                                handleItemsPerPageChange(value);
                              }}
                              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-h-[40px]"
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                              <option value="all">All</option>
                            </select>
                          </div>
                          <div className="text-sm text-gray-600">
                            {itemsPerPage === 'all' 
                              ? `Showing all ${pagination.total} leads`
                              : `Showing ${Math.min((currentPage - 1) * itemsPerPage + 1, pagination.total)}-${Math.min(currentPage * itemsPerPage, pagination.total)} of ${pagination.total} leads`
                            }
                          </div>
                        </div>

                        {/* Page navigation */}
                        {itemsPerPage !== 'all' && pagination.pages > 1 && (
                          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <div className="text-sm text-gray-600 text-center sm:text-left">
                              Page {currentPage} of {pagination.pages}
                            </div>
                            <div className="flex justify-center sm:justify-start space-x-2">
                              <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage <= 1}
                                className={`px-4 py-2 min-h-[44px] text-sm font-medium rounded-lg border transition-colors
                                  ${currentPage <= 1 
                                    ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 active:bg-gray-100'
                                  }`}
                              >
                                Previous
                              </button>
                              <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage >= pagination.pages}
                                className={`px-4 py-2 min-h-[44px] text-sm font-medium rounded-lg border transition-colors
                                  ${currentPage >= pagination.pages
                                    ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 active:bg-gray-100'
                                  }`}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <CustomizeFieldsDialog 
        isOpen={isCustomizeFieldsOpen}
        onClose={() => setIsCustomizeFieldsOpen(false)}
        columns={activeColumns}
        onToggleColumn={handleColumnToggle}
        onApply={handleApplyColumns}
        type="leads"
      />

      <NewLead 
        isOpen={isNewLeadOpen}
        onClose={() => setIsNewLeadOpen(false)}
      />

      <LeadDetailsDialog
        isOpen={isLeadDetailsOpen}
        onClose={() => setIsLeadDetailsOpen(false)}
        lead={selectedLead}
      />
    </DashboardLayout>
  );
}

export default LeadsAndEstimates; 